import { supabase, unwrap } from '@/services/supabase/client'
import type { CatDescription, IdentityCandidate, MatchDecision } from '@/types/models'

/**
 * Asks the engine who this might be. The embedding is produced on the device;
 * the ranking, and every decision about what may be shown, happens in the
 * database. A candidate belonging to somebody the viewer cannot see simply does
 * not come back. (§17)
 */
export async function findIdentityCandidates(input: {
  mediaId: string
  embedding: number[]
  modelVersion: string
  lat?: number | null
  lng?: number | null
  description?: CatDescription | null
  limit?: number
  /** Registering a house/friend cat: compare only against the domestic world. */
  home?: boolean
}): Promise<IdentityCandidate[]> {
  const traits = input.description
    ? {
        breed_or_type: input.description.breed_or_type,
        coat: input.description.coat,
        eye_color: input.description.eye_color,
        traits: input.description.traits,
      }
    : {}

  return unwrap(
    await supabase.rpc('find_identity_candidates', {
      p_media_id: input.mediaId,
      // pgvector accepts the textual form "[a,b,c]" over PostgREST.
      p_embedding: '[' + input.embedding.join(',') + ']',
      p_model_version: input.modelVersion,
      p_lat: input.lat ?? null,
      p_lng: input.lng ?? null,
      p_traits: traits,
      p_limit: input.limit ?? 3,
      p_home: input.home ?? false,
    }),
  ) as IdentityCandidate[]
}

export async function storeMediaEmbedding(input: {
  mediaId: string
  embedding: number[]
  modelVersion: string
  quality?: number
  catId?: string
}): Promise<string | null> {
  return unwrap(
    await supabase.rpc('store_media_embedding', {
      p_media_id: input.mediaId,
      p_embedding: '[' + input.embedding.join(',') + ']',
      p_model_version: input.modelVersion,
      p_quality: input.quality ?? null,
      p_cat_id: input.catId ?? null,
    }),
  ) as string | null
}

export async function confirmIdentityMatch(input: {
  candidateId: string
  decision: MatchDecision
  sightingId?: string
}): Promise<{ cat_id: string; status: string }> {
  return unwrap(
    await supabase.rpc('confirm_identity_match', {
      p_candidate_id: input.candidateId,
      p_decision: input.decision,
      p_sighting_id: input.sightingId ?? null,
    }),
  ) as { cat_id: string; status: string }
}

export async function mergeCats(sourceCatId: string, targetCatId: string) {
  return unwrap(
    await supabase.rpc('merge_cats', { p_source: sourceCatId, p_target: targetCatId }),
  ) as { merge_id: string; cat_id: string }
}

export async function unmergeCats(mergeId: string) {
  return unwrap(await supabase.rpc('unmerge_cats', { p_merge_id: mergeId })) as {
    source_cat_id: string
    target_cat_id: string
  }
}

export interface DuplicatePair {
  link_id: string
  /** La ficha más antigua: la que manda si se unen. */
  cat_manda: string
  /** La que se plegaría dentro. */
  cat_pliega: string
  score: number
  proposed_by: string | null
  proposer_username: string | null
  /** 'motor' lo vio la app; 'decides' te toca a ti; 'esperando' lo propusiste tú. */
  papel: 'motor' | 'decides' | 'esperando'
  detected_at: string
  /** Usuario de quien registró cada ficha, para no confundir dos «Don Turing». */
  manda_de: string | null
  pliega_de: string | null
  /** Las llaves que faltan: usuarios de quien registró cada ficha y aún no ha dicho que sí. */
  faltan: string[]
}

export async function listPossibleDuplicates(): Promise<DuplicatePair[]> {
  return (unwrap(await supabase.rpc('list_duplicate_pairs')) ?? []) as DuplicatePair[]
}

/**
 * «Este gato es el mismo que ese otro». Si las dos fichas son solo tuyas se
 * unen al momento; si la otra la tiene alguien más, se le pregunta y hasta que
 * conteste no se toca nada. Manda siempre la ficha más antigua. (§0074)
 */
export async function proposeSameCat(myCatId: string, otherCatId: string) {
  return unwrap(
    await supabase.rpc('propose_same_cat', { p_mine: myCatId, p_other: otherCatId }),
  ) as { merged: boolean; link_id?: string; merge_id?: string; cat_id?: string; faltan?: string[] }
}

export interface RecentMerge {
  merge_id: string
  cat_id: string
  nombre: string | null
  nombre_plegado: string | null
  performed_at: string
}

/** Las uniones de los últimos treinta días que puedes deshacer. */
export async function listRecentMerges(): Promise<RecentMerge[]> {
  return (unwrap(await supabase.rpc('list_recent_merges')) ?? []) as RecentMerge[]
}

export async function resolveDuplicate(
  linkId: string,
  decision: MatchDecision,
): Promise<{ status: string; falta?: string | null }> {
  return unwrap(
    await supabase.rpc('resolve_duplicate_link', { p_link_id: linkId, p_decision: decision }),
  ) as { status: string; falta?: string | null }
}

/**
 * Joining a friend's street cat: copy their record (name, sex, notes,
 * markings) onto my fresh row as a starting point. My edits afterwards are
 * mine alone; theirs stay theirs. No-op for my own cats and house cats.
 */
export async function adoptCatRecord(catId: string, fromOwnerId: string): Promise<void> {
  unwrap(
    await supabase.rpc('adopt_cat_record', {
      p_cat_id: catId,
      p_from_owner: fromOwnerId,
    }),
  )
}
